import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useT } from './LocaleContext';
import { GlobalStyle } from './styles/GlobalStyles';
import { validateEmail, getErrorMessage } from './common';
import { authAPI } from './api';
import { 
  VerificationContainer, 
  Message, 
  Title, 
  Text, 
  Form,
  Input, 
  Button,
  ResendLink,
  LoadingSpinner
} from './styles/ActivationPageStyles';


const ActivationPage = () => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [emailInput, setEmailInput] = useState('');
  // Получаем email из state или из query параметров (только для удобства при редиректе с регистрации)
  const email = location.state?.email || new URLSearchParams(location.search).get('email') || '';
  const fromRegistration = location.state?.fromRegistration || false;
  const fromLogin = location.state?.fromLogin || false;

  useEffect(() => {
    // Если email есть в URL или state, предзаполняем поле
    if (email) {
      setEmailInput(email);
    }
    if (fromLogin) {
      setMessage({
        text: t('activation.notActivated'),
        success: false,
      });
    }
  }, [email, fromLogin, t]);

  // Email from registration or login redirect is locked; otherwise editable.
  const emailLocked = Boolean((fromRegistration || fromLogin) && email);
  const currentEmail = emailLocked ? email : emailInput;

  const handleActivation = async () => {
    if (loading || resending || activating) {
      return;
    }
    if (!activationCode) {
      setMessage({ text: t('activation.enterCode'), success: false });
      return;
    }

    if (!currentEmail) {
      setMessage({ text: t('activation.enterEmail'), success: false });
      return;
    }

    const emailValidation = validateEmail(currentEmail.trim());
    if (!emailValidation.valid) {
      setMessage({ text: emailValidation.message, success: false });
      return;
    }

    setLoading(true);

    try {
      await authAPI.activate({
        email: currentEmail.trim(),
        code: activationCode.trim()
      });
      
      setActivating(true);
      // Добавляем задержку, чтобы показать спиннер
      await new Promise(resolve => setTimeout(resolve, 2000));
      navigate('/login');
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('activation.activationError'));
      setMessage({ text: errorMessage, success: false });
      setLoading(false);
      setActivating(false);
    }
  };

  const handleResendCode = async () => {
    if (!currentEmail) {
      setMessage({ text: t('validation.emailRequired'), success: false });
      return;
    }

    const emailValidation = validateEmail(currentEmail.trim());
    if (!emailValidation.valid) {
      setMessage({ text: emailValidation.message, success: false });
      return;
    }

    setResending(true);
    setMessage(null);

    try {
      await authAPI.resendActivation(currentEmail.trim());
      setMessage({ text: t('activation.codeSentTo', { email: currentEmail.trim() }), success: true });
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('activation.activationError'));
      setMessage({ text: errorMessage, success: false });
    } finally {
      setResending(false);
    }
  };

  return (
    <VerificationContainer>
      <GlobalStyle />
      {message && <Message success={message.success}>{message.text}</Message>}
      
      {activating && (
        <>
          <LoadingSpinner />
          <Text style={{ marginTop: '20px' }}>{t('activation.activatedTitle')}</Text>
          <Text style={{ marginTop: '10px', fontSize: '0.9em', color: '#6b7280' }}>{t('activation.redirectingLogin')}</Text>
        </>
      )}
      
      {!activating && (
        <>
          {fromRegistration && (
            <>
              <Title>{t('activation.registrationSuccess')}</Title>
              <Text>
                {t('activation.accountCreated')}
              </Text>
              <Text>
                {t('activation.codeSentTo', { email: currentEmail })}
              </Text>
              <Text style={{ marginBottom: '24px' }}>
                {t('activation.checkInbox')}
              </Text>
            </>
          )}
          
          {!fromRegistration && fromLogin && (
            <>
              <Title>{t('activation.activateTitle')}</Title>
              <Text style={{ marginBottom: '24px' }}>
                {t('activation.checkInbox')}
              </Text>
            </>
          )}

          {!fromRegistration && !fromLogin && (
            <Title>{t('activation.enterCodeTitle')}</Title>
          )}

          <Form onSubmit={(e) => { e.preventDefault(); handleActivation(); }}>
            <Input
              type="text"
              placeholder={t('common.email')}
              value={emailLocked ? email : emailInput}
              maxLength={254}
              autoComplete="email"
              onChange={(e) => setEmailInput(e.target.value.trim())}
              disabled={emailLocked}
              style={{ marginBottom: '10px', ...(emailLocked && { opacity: 0.6, cursor: 'not-allowed' }) }}
            />
            <Input
              type="text"
              placeholder={t('common.code')}
              value={activationCode}
              maxLength={10}
              inputMode="numeric"
              onChange={(e) => setActivationCode(e.target.value.trim())}
              $narrow
              style={{ marginBottom: '10px' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && activationCode && currentEmail) {
                  e.preventDefault();
                  handleActivation();
                }
              }}
            />
            <Button as="button" type="submit" disabled={loading || resending || !currentEmail}>
              {loading ? t('activation.activating') : t('activation.activate')}
            </Button>
            {currentEmail && (
              <Text style={{ marginTop: '16px', fontSize: '0.9em', color: '#6b7280' }}>
                {t('activation.didNotReceive')}{' '}
                <ResendLink type="button" onClick={handleResendCode} disabled={resending}>
                  {resending ? t('activation.sending') : t('activation.resend')}
                </ResendLink>
              </Text>
            )}
          </Form>
        </>
      )}
    </VerificationContainer>
  );
};

export default ActivationPage;
